import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { registerPostCriticalCallback } from '@/lib/performance/postCriticalGate';
import { isPartnerPortalPathname } from '@/routes';

/**
 * ThirdPartyBootstrap - Deferred consent-script loading
 *
 * Google Tag Manager lives in `app/root.tsx` so it is present on every page
 * in the document shell. Do not reintroduce deferred GTM loading here.
 */
function ThirdPartyBootstrap() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (new URLSearchParams(search).get('embed_editor_preview') === 'true') {
      console.debug('Skipping third-party bootstrap for embed editor preview');
      return;
    }

    if (isPartnerPortalPathname(pathname)) {
      return;
    }

    // Google's consent kernel does not initialize in same-origin frames.
    // The parent owns consent there; GTM still loads in every document.
    if (window.frameElement) {
      return;
    }

    const unregisterFundingChoices = registerPostCriticalCallback(
      'funding-choices-bootstrap',
      loadFundingChoices,
      'high'
    );

    return () => {
      unregisterFundingChoices();
    };
  }, [pathname, search]);

  return null; // This component handles side-effects only
}

function loadFundingChoices(): void {
  if (document.getElementById('funding-choices-script')) {
    console.debug('FundingChoices script already exists');
    return;
  }

  try {
    window.googlefc = window.googlefc || {};
    window.googlefc.callbackQueue = window.googlefc.callbackQueue || [];

    const script = document.createElement('script');
    script.id = 'funding-choices-script';
    script.src = 'https://fundingchoicesmessages.google.com/i/pub-8416401846941251?ers=1';
    script.async = true;

    script.onload = () => {
      console.debug('FundingChoices loaded successfully');
    };

    script.onerror = () => {
      console.error('Failed to load FundingChoices');
    };

    document.head.appendChild(script);
  } catch (error) {
    console.error('Error loading FundingChoices:', error);
  }
}

export default ThirdPartyBootstrap;
